import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FormField, Input } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import { leadsApi } from "@/api/leads";
import type { LeadDuplicateMatchDto } from "@/api/leads";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Editable Lead fields shown in the AI Create+ review step. */
export interface LeadFormState {
  firstName: string;
  lastName: string;
  companyName: string;
  title: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  notes: string;
}

interface Props {
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  form: LeadFormState;
  onFormChange: (next: LeadFormState) => void;
}

/**
 * Calendar AI Create+ — "Also create a Lead" section. Rendered only when the
 * parse surfaced a lead AND the user holds Leads.Create (gated by the parent).
 * Default OFF; when enabled it shows the editable lead fields pre-filled from the
 * draft, a non-blocking duplicate notice, and inline phone/email format hints.
 * Source/InterestLevel default server-side; NextFollowUpDate is set to the
 * appointment start by the submit handler.
 */
export function LeadSuggestionSection({
  enabled,
  onEnabledChange,
  form,
  onFormChange,
}: Props) {
  const [dupes, setDupes] = useState<LeadDuplicateMatchDto[]>([]);
  const fetchedRef = useRef(false);

  const set = (patch: Partial<LeadFormState>) => onFormChange({ ...form, ...patch });

  // Fire the duplicate check once, when the section is first enabled.
  useEffect(() => {
    if (!enabled || fetchedRef.current) return;
    const phone = form.phone.trim() || undefined;
    const email = form.email.trim() || undefined;
    const company = form.companyName.trim() || undefined;
    if (!phone && !email && !company) return;
    fetchedRef.current = true;
    let cancelled = false;
    void leadsApi
      .duplicates(phone, email, company)
      .then((rows) => {
        if (!cancelled) setDupes(rows);
      })
      .catch(() => {
        /* dedup is advisory — ignore failures */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, form.phone, form.email, form.companyName]);

  const emailError =
    form.email.trim() && !EMAIL_RE.test(form.email.trim())
      ? "Email format looks off."
      : undefined;
  const phoneError =
    form.phone.trim() && form.phone.replace(/\D/g, "").length < 10
      ? "Phone looks too short."
      : undefined;

  return (
    <div className="rounded-lg border border-border-light bg-white-warm p-space-3">
      <label className="flex items-center gap-space-2 font-display text-small font-semibold text-onyx">
        <input
          type="checkbox"
          className="accent-gold"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
        />
        Also create a Lead from this text
      </label>
      <p className="mb-0 mt-space-1 text-caption text-gray-mid">
        Capture this contact as a prospective broker in the CRM. The appointment
        is linked to the new lead.
      </p>

      {enabled && (
        <div className="mt-space-3 flex flex-col gap-space-3">
          {dupes.length > 0 && (
            <div className="rounded-md border border-gold/40 bg-gold/10 px-space-3 py-space-2 text-caption text-onyx">
              Similar lead(s) already exist —{" "}
              <Link to="/leads" className="underline">
                check the leads list
              </Link>{" "}
              before creating.
            </div>
          )}
          <div className="grid grid-cols-1 gap-space-3 sm:grid-cols-2">
            <FormField label="First name">
              <Input
                value={form.firstName}
                onChange={(e) => set({ firstName: e.target.value })}
              />
            </FormField>
            <FormField label="Last name">
              <Input
                value={form.lastName}
                onChange={(e) => set({ lastName: e.target.value })}
              />
            </FormField>
            <FormField label="Company">
              <Input
                value={form.companyName}
                onChange={(e) => set({ companyName: e.target.value })}
              />
            </FormField>
            <FormField label="Title">
              <Input
                value={form.title}
                onChange={(e) => set({ title: e.target.value })}
              />
            </FormField>
            <FormField label="Phone" error={phoneError}>
              <Input
                value={form.phone}
                invalid={Boolean(phoneError)}
                onChange={(e) => set({ phone: e.target.value })}
              />
            </FormField>
            <FormField label="Email" error={emailError}>
              <Input
                value={form.email}
                invalid={Boolean(emailError)}
                onChange={(e) => set({ email: e.target.value })}
              />
            </FormField>
            <FormField label="City">
              <Input value={form.city} onChange={(e) => set({ city: e.target.value })} />
            </FormField>
            <FormField label="State">
              <Input
                value={form.state}
                maxLength={2}
                onChange={(e) => set({ state: e.target.value.toUpperCase().slice(0, 2) })}
              />
            </FormField>
          </div>
          <FormField label="Notes">
            <Textarea
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
            />
          </FormField>
        </div>
      )}
    </div>
  );
}
